<template>
    <div class="avatar-3d-picker">
        <div ref="viewport" class="avatar-3d-viewport" :style="{ width: size + 'px', height: size + 'px' }"></div>
        <div class="avatar-3d-swatches" v-if="interactive">
            <button
                type="button"
                v-for="option in colorOptions"
                :key="option.hex"
                class="avatar-3d-swatch"
                :class="{
                    'avatar-3d-swatch-selected': option.hex === modelValue,
                    'avatar-3d-swatch-taken': is_taken(option.hex)
                }"
                :style="{ backgroundColor: option.hex }"
                :title="is_taken(option.hex) ? option.label + ' (ya cogido)' : option.label"
                :disabled="is_taken(option.hex)"
                @click="select_color(option.hex)"
            ></button>
        </div>
    </div>
</template>

<script>
import { load_model } from '@/utils/model-cache'
import { center_model, fit_model_camera } from '@/utils/model-framing'

// Generic 3D-preview color picker: loads any glTF/GLB model and lets the
// caller offer a palette to recolor it with. Knows nothing about jerseys/
// "maillots" specifically - the model URL and palette are entirely supplied
// via props by whoever uses it (see Auth.vue for the jersey-specific wiring).
export default {
    name: 'Avatar3DPicker',
    props: {
        modelUrl: {
            type: String,
            required: true,
        },
        colorOptions: {
            type: Array,
            required: true,
        },
        modelValue: {
            type: String,
            default: null,
        },
        // Hex values to show as unavailable (still visible, just disabled) -
        // e.g. colors another player has already picked. Entirely optional;
        // the component has no opinion on why a color would be taken.
        takenColors: {
            type: Array,
            default: () => [],
        },
        // Purely decorative uses (a "here's your jersey" display with
        // nothing to pick, e.g. Lobby.vue) don't need the swatch row at all.
        interactive: {
            type: Boolean,
            default: true,
        },
        size: {
            type: Number,
            default: 260,
        }
    },
    emits: ['update:modelValue'],
    data(){
        return {
            loading: true,
            load_error: false,
        };
    },
    watch: {
        modelValue(hex){
            this.apply_color(hex);
        }
    },
    async mounted(){
        // Loaded dynamically so three.js's bundle weight only lands on
        // whichever route actually renders this component (Auth.vue's
        // jersey step), not every other page - but cached/shared via
        // load_model(), so it only ever downloads+parses once per URL no
        // matter how many pickers mount (Auth.vue's interactive one, plus
        // Lobby.vue's decorative one), and main.js warms it up at boot so
        // this usually resolves immediately instead of visibly loading.
        const { THREE, gltf } = await load_model(this.modelUrl);
        this._THREE = THREE;

        const viewport = this.$refs.viewport;
        if(!viewport)
            return;

        const width = viewport.clientWidth || 260;
        const height = viewport.clientHeight || 260;

        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100);
        fit_model_camera(this.camera);

        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        this.renderer.setSize(width, height);
        // Opaque background (rather than the previous alpha:true transparent
        // canvas) so a still-blank result after this change means the
        // renderer itself isn't painting anything, not just a camera/model
        // framing problem - narrows down the next fix.
        this.renderer.setClearColor(0xe9edf0, 1);
        viewport.appendChild(this.renderer.domElement);

        this.scene.add(new THREE.AmbientLight(0xffffff, 0.7));
        const key_light = new THREE.DirectionalLight(0xffffff, 0.9);
        key_light.position.set(2, 3, 4);
        this.scene.add(key_light);
        const fill_light = new THREE.DirectionalLight(0xffffff, 0.4);
        fill_light.position.set(-3, 1, -2);
        this.scene.add(fill_light);

        this.materials = [];
        this._resize_observer = new ResizeObserver(() => this.handle_resize());
        this._resize_observer.observe(viewport);

        try{
            // gltf.scene is shared (cached by load_model above) across every
            // picker instance - clone it so this instance's centering/scale
            // mutations below never affect another mounted picker using the
            // same cached model.
            const model = gltf.scene.clone(true);

            model.traverse((node) => {
                if(node.isMesh && node.material){
                    // clone(true) above only deep-clones the Object3D
                    // hierarchy/transforms - materials are still the SAME
                    // shared objects as every other instance's clone. Clone
                    // them too (and reassign onto the node) so apply_color()
                    // tinting this instance's jersey never bleeds into
                    // another mounted picker's (e.g. Lobby.vue's decorative
                    // one) sharing the same cached model.
                    const was_array = Array.isArray(node.material);
                    const source_mats = was_array ? node.material : [node.material];
                    const cloned_mats = source_mats.map((mat) => {
                        const cloned = mat.clone();
                        // obj2gltf conversions (this asset's own "asset.generator",
                        // confirmed by inspecting the .glb directly) commonly ship
                        // normals/winding that make a single-sided material
                        // invisible from outside the mesh - safe to always
                        // double-side a small preview garment like this one.
                        cloned.side = THREE.DoubleSide;
                        if(cloned.color)
                            this.materials.push(cloned);
                        return cloned;
                    });
                    node.material = was_array ? cloned_mats : cloned_mats[0];
                }
            });

            const pivot = center_model(THREE, model);

            this.scene.add(pivot);
            this.model = pivot;
            // Default to the first available color rather than always
            // colorOptions[0], which could already be taken.
            const default_option = this.colorOptions.find((o) => !this.is_taken(o.hex)) || this.colorOptions[0];
            const initial_hex = (this.modelValue && !this.is_taken(this.modelValue)) ? this.modelValue : (default_option && default_option.hex);
            if(initial_hex !== this.modelValue)
                this.$emit('update:modelValue', initial_hex);
            this.apply_color(initial_hex);
            this.loading = false;
        }catch(ex){
            console.error(ex);
            this.load_error = true;
            this.loading = false;
        }

        this._animating = true;
        this.animate();
    },
    beforeUnmount(){
        this._animating = false;
        if(this._resize_observer)
            this._resize_observer.disconnect();
        if(this.renderer){
            this.renderer.dispose();
            if(this.renderer.domElement && this.renderer.domElement.parentNode)
                this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
        (this.materials || []).forEach((mat) => mat.dispose && mat.dispose());
    },
    methods: {
        select_color(hex){
            if(this.is_taken(hex))
                return;
            this.$emit('update:modelValue', hex);
            this.apply_color(hex);
        },
        is_taken(hex){
            return this.takenColors.includes(hex);
        },
        apply_color(hex){
            if(!hex || !this.materials)
                return;
            this.materials.forEach((mat) => mat.color.set(hex));
        },
        handle_resize(){
            const viewport = this.$refs.viewport;
            if(!viewport || !this.renderer || !this.camera)
                return;
            const width = viewport.clientWidth || 260;
            const height = viewport.clientHeight || 260;
            this.renderer.setSize(width, height);
            this.camera.aspect = width / height;
            fit_model_camera(this.camera);
        },
        animate(){
            if(!this._animating)
                return;
            requestAnimationFrame(this.animate);
            if(this.model)
                this.model.rotation.y += 0.006;
            if(this.renderer && this.scene && this.camera)
                this.renderer.render(this.scene, this.camera);
        }
    }
}
</script>

<style scoped>
    .avatar-3d-picker{
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .avatar-3d-viewport{
        max-width: 100%;
    }

    .avatar-3d-swatches{
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.6rem;
        margin-top: 0.75rem;
    }

    .avatar-3d-swatch{
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 2px solid var(--vc-line, #ddd);
        cursor: pointer;
        padding: 0;
    }

    .avatar-3d-swatch-selected{
        border-color: var(--vc-accent-500, #00a7af);
        box-shadow: 0 0 0 2px rgba(0, 167, 175, 0.3);
    }

    .avatar-3d-swatch-taken{
        cursor: not-allowed;
        opacity: 0.3;
        position: relative;
    }
    .avatar-3d-swatch-taken::after{
        content: "";
        position: absolute;
        top: 50%;
        left: 10%;
        right: 10%;
        border-top: 2px solid rgba(0, 0, 0, 0.6);
        transform: translateY(-50%) rotate(-45deg);
    }
</style>
